// src/pages/erp/FiscalSettings.tsx
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button, SegmentedTabs } from "@akuna/ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";
import { PermissionGuard } from "@/components/PermissionGuard";
import { GeneralTab } from "./fiscal/GeneralTab";
import { NfseTab } from "./fiscal/NfseTab";
import { NfceTab } from "./fiscal/NfceTab";
import type { FocusEmpresaResponse, LocalFiscalDefaults, FiscalTabProps } from "./fiscal/fiscal-settings.types";

interface FiscalSettingsProps {
  showHeader?: boolean;
  backTo?: string;
}

const FOCUS_ENV = (import.meta.env.VITE_FOCUSNFE_ENV ?? "homologacao") as "homologacao" | "producao";

export default function FiscalSettings({ showHeader = true, backTo = "/sales" }: FiscalSettingsProps) {
  const { t } = useTranslation("erp");
  const [isLoading, setIsLoading] = useState(true);
  const [tabProps, setTabProps] = useState<FiscalTabProps | null>(null);
  const [activeTab, setActiveTab] = useState("general");

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const customerId = await getCurrentClinicCustomerId();

        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError || !userData.user) throw new Error(t("fiscalSettings.errors.unauthenticated"));

        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("current_clinic_id")
          .eq("id", userData.user.id)
          .single();

        if (profileError || !profile?.current_clinic_id) throw new Error(t("fiscalSettings.errors.noClinicSelected"));
        const clinicId = profile.current_clinic_id;

        const { data: clinic } = await supabase
          .from("clinics")
          .select("cnpj")
          .eq("id", clinicId)
          .maybeSingle();

        // Strip mask chars only — preserve alphanumeric (CNPJ alfanumérico Jul/2026)
        const cnpj = String(clinic?.cnpj ?? "").replace(/[.\-/\s]/g, "").toUpperCase();

        // Load local fiscal settings (contains focus_empresa_id)
        const { data: localData } = await supabase
          .from("clinic_fiscal_settings")
          .select("*")
          .eq("clinic_id", clinicId)
          .maybeSingle();

        const focusEmpresaId = localData?.focus_empresa_id ?? null;

        // Fetch empresa from FocusNFe only if we have a stored id
        let focusData: FocusEmpresaResponse | null = null;
        if (focusEmpresaId) {
          const { data: focusResult } = await supabase.functions.invoke<FocusEmpresaResponse | { notFound: true }>(
            "fiscal-manage-empresa",
            { body: { method: "GET", focus_empresa_id: focusEmpresaId } },
          );
          if (focusResult && !("notFound" in focusResult)) {
            focusData = focusResult as FocusEmpresaResponse;
          }
        }

        setTabProps({
          cnpj,
          environment: FOCUS_ENV,
          focusData,
          focusEmpresaId,
          localDefaults: localData,
          clinicId,
          customerId,
        });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t("fiscalSettings.errors.loadError"));
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [t]);

  return (
    <PermissionGuard resource="sales" action="edit">
      <div className="space-y-6">
        {showHeader && (
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t("fiscalSettings.title")}</h1>
            <p className="text-muted-foreground mt-2">{t("fiscalSettings.description")}</p>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center min-h-[240px]">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : tabProps ? (
          <SegmentedTabs
            value={activeTab}
            onValueChange={setActiveTab}
            items={[
              {
                value: "general",
                label: <>{t("fiscalSettings.tabs.general")}</>,
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle>{t("fiscalSettings.tabs.general")}</CardTitle>
                      <CardDescription>{t("fiscalSettings.general.description")}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <GeneralTab {...tabProps} />
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "nfse",
                label: <>{t("fiscalSettings.tabs.nfse")}</>,
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle>{t("fiscalSettings.tabs.nfse")}</CardTitle>
                      <CardDescription>{t("fiscalSettings.nfse.description")}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <NfseTab {...tabProps} />
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "nfce",
                label: <>{t("fiscalSettings.tabs.nfce")}</>,
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle>{t("fiscalSettings.tabs.nfce")}</CardTitle>
                      <CardDescription>{t("fiscalSettings.nfce.description")}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <NfceTab {...tabProps} />
                    </CardContent>
                  </Card>
                ),
              },
            ]}
          />
        ) : null}

        <div className="flex">
          <Button type="button" variant="outline" asChild>
            <Link to={backTo}>{t("fiscalSettings.back")}</Link>
          </Button>
        </div>
      </div>
    </PermissionGuard>
  );
}
